import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { GetBlockParams } from '@/pages/Content/components/BlockList/BlockFilters';
import { GetCollectionParams } from '@/pages/Content/components/CollectionsList/CollectionFilters';
import { ExploreBlockType } from '@addmile/backend-client';

export type ContentTab = 'collections' | 'content';

export const DEFAULT_COLLECTION_FILTERS: GetCollectionParams = {
  title: '',
  language: undefined,
  status: [],
  pageSize: 20,
};

export const DEFAULT_BLOCK_FILTERS: GetBlockParams = {
  title: '',
  type: [],
  language: undefined,
  status: [],
  cursor: undefined,
  pageSize: 24,
};

export const DEFAULT_COLLECTION_BLOCK_FILTERS: GetBlockParams = {
  title: '',
  type: [],
  language: undefined,
  status: [],
};

export type ContentViewMode =
  | 'list'
  | 'editor'
  | 'createCollection'
  | 'editCollection'
  | 'collectionBlocks';

interface ContentViewState {
  viewMode: ContentViewMode;
  editingSlug?: string;
  draftType?: ExploreBlockType;
  editCollectionSlug?: string;
  collectionBlocksSlug?: string;
  showCopyBlockModal: boolean;
  copyBlockSlug?: string;

  // Browsing position, deliberately outside resetView so opening a block and coming back lands
  // the editor exactly where they left off.
  activeTab: ContentTab;
  collectionFilters: GetCollectionParams;
  collectionPage: number;
  blockFilters: GetBlockParams;
  blockPage: number;
  collectionBlockFilters: GetBlockParams;
  // Where the editor's back and save lead: a block opened from a collection returns to it.
  editorReturnView: 'list' | 'collectionBlocks';

  setActiveTab: (tab: ContentTab) => void;
  setCollectionFilters: (filters: GetCollectionParams) => void;
  setCollectionPage: (page: number) => void;
  setBlockFilters: (filters: GetBlockParams) => void;
  setBlockPage: (page: number) => void;
  setCollectionBlockFilters: (filters: GetBlockParams) => void;
  setEditorReturnView: (view: 'list' | 'collectionBlocks') => void;

  setShowCopyBlockModal: (show: boolean) => void;
  setViewMode: (mode: ContentViewMode) => void;
  setEditingSlug: (slug?: string) => void;
  setDraftType: (type?: ExploreBlockType) => void;
  setEditCollectionSlug: (slug?: string) => void;
  setCollectionBlocksSlug: (slug?: string) => void;
  resetView: () => void;
}

export const useContentViewStore = create<ContentViewState>()(
  persist(
    immer((set) => ({
      viewMode: 'list' as ContentViewMode,
      editingSlug: undefined,
      draftType: undefined,
      editCollectionSlug: undefined,
      collectionBlocksSlug: undefined,
      showCopyBlockModal: false,
      copyBlockSlug: undefined,

      activeTab: 'collections' as ContentTab,
      collectionFilters: DEFAULT_COLLECTION_FILTERS,
      collectionPage: 0,
      blockFilters: DEFAULT_BLOCK_FILTERS,
      blockPage: 0,
      collectionBlockFilters: DEFAULT_COLLECTION_BLOCK_FILTERS,
      editorReturnView: 'list',

      setActiveTab: (tab) => set((state) => void (state.activeTab = tab)),
      setCollectionFilters: (filters) => set((state) => void (state.collectionFilters = filters)),
      setCollectionPage: (page) => set((state) => void (state.collectionPage = page)),
      setBlockFilters: (filters) => set((state) => void (state.blockFilters = filters)),
      setBlockPage: (page) => set((state) => void (state.blockPage = page)),
      setCollectionBlockFilters: (filters) =>
        set((state) => void (state.collectionBlockFilters = filters)),
      setEditorReturnView: (view) => set((state) => void (state.editorReturnView = view)),

      setShowCopyBlockModal: (show) => set((state) => void (state.showCopyBlockModal = show)),
      setViewMode: (mode) => set((state) => void (state.viewMode = mode)),
      setEditingSlug: (slug) => set((state) => void (state.editingSlug = slug)),
      setDraftType: (type) => set((state) => void (state.draftType = type)),
      setEditCollectionSlug: (slug) => set((state) => void (state.editCollectionSlug = slug)),
      setCollectionBlocksSlug: (slug) => set((state) => void (state.collectionBlocksSlug = slug)),
      resetView: () =>
        set((state) => {
          state.viewMode = 'list';
          state.editingSlug = undefined;
          state.draftType = undefined;
          state.editCollectionSlug = undefined;
          state.collectionBlocksSlug = undefined;
        }),
    })),
    {
      name: 'content-view-storage',
    },
  ),
);
